import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router";
import {
  ArrowRight,
  CalendarDays,
  Clock3,
  FileText,
  Mail,
  MapPin,
  ShieldCheck,
  Sparkle,
  Tag,
  UsersRound,
} from "lucide-react";

import { PUBLIC_FOCUS, PUBLIC_LINK } from "@/components/public/control-classes";
import { LandingHeader } from "@/components/public/LandingHeader";
import { RhythmRail } from "@/components/public/RhythmRail";
import type { AcademyLandingContent } from "@/content/landing";
import { cn } from "@/lib/utils";

interface MurtadaAcademyLandingShellProps {
  applicationForm: ReactNode;
  brandName: string;
  logoUrl: string | null;
  parentOrg: string | null;
  content: AcademyLandingContent;
  showSignIn?: boolean;
}

const factIcons = [UsersRound, CalendarDays, Clock3, FileText];

/**
 * The descender's corner, one variant per horizontal export. Each is the
 * approved terminus vector for its own frame transposed across y=x -- the
 * terminus turns a horizontal run down, the descender turns a vertical run
 * right, and it is the same corner. So the rail turns in exactly one way
 * everywhere: a fixed arc off axis, a straight 45-degree diagonal, a fixed arc
 * back onto axis, each vertex rounded on a tangent of one stroke width.
 *
 * Two variants rather than one scaled pipe, because the corner's footprint is
 * one rail step in each direction and step over stroke is 44/12 at 1440
 * against 36/10 at 1024. The riser runs off the top and the run off the right;
 * both overshoot far enough that the window clips them.
 */
const DESCENDER_PIPES = [
  {
    frame: "desktop1024",
    viewBox: "0 0 41 41",
    width: 10,
    d: "M5 -100000V0C5 6.545 7.316 12.136 11.947 16.765L25.872 30.69C28.867 33.685 34.456 36 41 36H100000",
  },
  {
    frame: "desktop1440",
    viewBox: "0 0 50 50",
    width: 12,
    d: "M6 -100000V0C6 8 8.83 14.83 14.49 20.49L31.51 37.51C35.17 41.17 42 44 50 44H100000",
  },
] as const;

const applicationTheme = {
  "--background": "transparent",
  "--foreground": "var(--public-on-inverse)",
  // 30% read under 3:1 against the deep-green apply background (WCAG 1.4.11
  // non-text contrast) — the boxed fields' resting border needs to be
  // findable without relying on focus state.
  "--input": "color-mix(in oklab, var(--public-on-inverse) 45%, transparent)",
  "--border": "color-mix(in oklab, var(--public-on-inverse) 45%, transparent)",
  "--muted-foreground": "var(--public-on-inverse-accent)",
  "--ring": "var(--public-highlight)",
  "--public-foreground": "var(--public-on-inverse)",
  "--public-foreground-soft": "var(--public-on-inverse-accent)",
  "--public-readable-highlight": "var(--public-highlight)",
} as CSSProperties;

export function MurtadaAcademyLandingShell({
  applicationForm,
  brandName,
  logoUrl,
  parentOrg,
  content,
  showSignIn = true,
}: MurtadaAcademyLandingShellProps) {
  const applicationFormRef = useRef<HTMLDivElement>(null);
  const [applicationFormInView, setApplicationFormInView] = useState(false);
  const academy = content.academy;
  const academyAffiliation = academy.affiliation || parentOrg;
  const decisionFactValue = (label: string, preferMobile = false) => {
    const fact = academy.decisionFacts.find((item) => item.label === label);
    return preferMobile ? (fact?.mobileValue ?? fact?.value ?? "") : (fact?.value ?? "");
  };
  const mobileFitFacts = [
    {
      label: "Ages",
      value: decisionFactValue("Age range"),
      Icon: UsersRound,
    },
    {
      label: "Format",
      value: decisionFactValue("Format", true),
      Icon: MapPin,
    },
    {
      label: "Schedule",
      value: decisionFactValue("Weekly rhythm"),
      Icon: CalendarDays,
    },
    {
      label: "Tuition",
      value: decisionFactValue("Tuition"),
      Icon: Tag,
    },
  ];

  const decisionFacts = academy.decisionFacts.map((fact) => (
    <div
      key={fact.label}
      className="grid grid-cols-[108px_minmax(0,1fr)] gap-3.5 border-b border-[var(--public-on-inverse)]/[0.16] py-[9px] last:border-b-0 md:grid-cols-1 md:gap-0 md:py-[11px]"
    >
      <dt className="font-mono text-[9px] font-semibold uppercase tracking-[0.12em] text-[var(--public-on-inverse-accent)]/80 md:tracking-[0.14em]">
        {fact.label}
      </dt>
      <dd className="font-display text-[16px] leading-[1.3] text-[var(--public-on-inverse)] md:mt-[5px] md:text-[17px]">
        {fact.value}
      </dd>
    </div>
  ));

  useEffect(() => {
    const applicationFormElement = applicationFormRef.current;
    if (!applicationFormElement || !("IntersectionObserver" in window)) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setApplicationFormInView(
          entries.some((entry) => entry.isIntersecting),
        );
      },
      {
        rootMargin: "0px 0px -72px 0px",
        threshold: 0.05,
      },
    );

    observer.observe(applicationFormElement);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const targetId = window.location.hash.slice(1);
    if (!targetId) {
      return;
    }

    document.getElementById(targetId)?.scrollIntoView({
      behavior: "auto",
      block: "start",
    });
  }, []);

  return (
    <div
      id="top"
      className="min-h-screen scroll-smooth bg-[var(--public-background)] pb-[60px] text-[var(--public-foreground)] md:pb-0"
    >
      <LandingHeader
        brandName={brandName}
        logoUrl={logoUrl}
        parentOrg={academyAffiliation}
        navItems={[]}
        ctaHref="#apply"
        ctaLabel="Apply for the 2026-27 academic year"
        mobileCtaLabel="Apply"
        editorial
        showSignIn={false}
      />

      <main>
        <section
          aria-labelledby="academy-heading"
          className="overflow-hidden border-b border-[var(--public-rule)]"
        >
          <div className="relative flex flex-col md:block md:min-h-[555px]">
            <div
              className="absolute right-0 top-0 h-[400px] w-[60%] overflow-hidden bg-[var(--public-primary-deep)] [clip-path:polygon(42%_0,100%_0,100%_100%,12%_100%)] min-[480px]:h-[450px] sm:h-[440px] md:inset-y-0 md:h-auto md:w-[46%] md:[clip-path:polygon(22%_0,100%_0,100%_100%,0_100%)]"
              style={{
                backgroundImage: "url('/brand/murtada/geometric-field.jpg')",
                backgroundPosition: "58% center",
                backgroundSize: "cover",
              }}
            >
              <img
                src="/brand/murtada/logo-mark-green.png"
                alt=""
                aria-hidden="true"
                className="absolute left-[60%] top-[52%] w-[clamp(330px,88vw,600px)] max-w-none -translate-x-1/2 -translate-y-1/2 opacity-75 md:left-[68%] md:top-[49%] md:w-[min(118%,650px)]"
              />
            </div>

            <div className="order-1 relative z-10 mx-auto min-h-[400px] w-full max-w-[1240px] pb-8 pl-5 pr-[43%] pt-12 min-[480px]:min-h-[450px] sm:min-h-[440px] sm:pl-10 sm:pt-14 md:min-h-0 md:px-12 md:pb-[140px] md:pt-[58px]">
              <div className="max-w-[590px]">
                <h1
                  id="academy-heading"
                  aria-label={academy.hero.h1}
                  className="text-[clamp(30px,7.8vw,48px)] font-semibold leading-[0.96] tracking-[-0.035em] text-[var(--public-foreground)] min-[480px]:max-w-[300px] md:max-w-none md:text-[clamp(52px,6.2vw,64px)] md:leading-[0.96]"
                >
                  {academy.hero.lines.map((line, index) => (
                    <span key={line} className="block">
                      {line}
                      {index < academy.hero.lines.length - 1 ? " " : null}
                    </span>
                  ))}
                </h1>
                <p className="mt-6 max-w-[23ch] font-display text-[18px] italic leading-[1.3] text-[var(--public-readable-highlight)] md:mt-7 md:max-w-[31ch] md:text-[clamp(19px,2vw,23px)]">
                  {academy.hero.sub}
                </p>
                <p className="mt-4 hidden max-w-[min(55ch,44vw)] text-[14px] leading-[1.55] text-[var(--public-foreground-soft)] sm:text-[15px] md:block">
                  {academy.hero.mission}
                </p>
              </div>
            </div>

            <div
              className="order-3 relative z-20 bg-[var(--public-background)] md:absolute md:bottom-0 md:left-0 md:w-[71%]"
            >
              <dl
                className="hidden border-t border-[var(--public-rule)] px-5 sm:px-10 md:grid md:grid-cols-[0.75fr_1.15fr_1.15fr_1.25fr] md:px-[clamp(48px,5vw,64px)]"
              >
                {academy.hero.facts.map((fact, index) => {
                  const Icon = factIcons[index];
                  return (
                    <div
                      key={fact.label}
                      className="grid min-h-[104px] grid-cols-[auto_minmax(0,1fr)] content-center gap-x-3 border-b border-[var(--public-rule)] py-4 pr-3 even:pl-4 odd:border-r md:min-h-[130px] md:gap-x-3 md:border-b-0 md:border-r md:px-4 md:first:pl-0 md:last:border-r-0 md:last:pr-0"
                    >
                      <dt className="col-span-2 grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 font-mono text-[8px] font-semibold uppercase tracking-[0.16em] text-[var(--public-foreground-muted)] md:gap-x-3">
                        <Icon
                          className="size-6 shrink-0 text-[var(--public-highlight)] md:size-7"
                          strokeWidth={1.55}
                          aria-hidden="true"
                        />
                        <span className="min-w-0">{fact.label}</span>
                      </dt>
                      <dd className="mt-1 min-w-0 font-mono text-[10px] font-semibold uppercase leading-[1.42] tracking-[0.05em] text-[var(--public-foreground)] md:text-[10.5px]">
                        {fact.value}
                      </dd>
                    </div>
                  );
                })}
              </dl>

              <section
                aria-labelledby="mobile-fit-heading"
                className="border-t border-[var(--public-rule)] px-5 py-8 md:hidden"
              >
                <div className="flex items-center gap-5">
                  <h2
                    id="mobile-fit-heading"
                    className="shrink-0 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--public-primary-deep)]"
                  >
                    Is Murtada a fit?
                  </h2>
                  <span
                    className="h-px flex-1 bg-[var(--public-rule)]"
                    aria-hidden="true"
                  />
                </div>

                <dl className="mt-4">
                  {mobileFitFacts.map(({ label, value, Icon }) => (
                    <div
                      key={label}
                      className="grid min-h-[68px] grid-cols-[44px_minmax(0,1fr)] content-center gap-x-4 border-b border-[var(--public-rule)]"
                    >
                      <dt className="col-span-2 grid min-w-0 grid-cols-[44px_minmax(0,1fr)] items-center gap-x-4 font-mono text-[9px] font-semibold uppercase tracking-[0.16em] text-[var(--public-primary-deep)]">
                        <Icon
                          className="size-7 shrink-0 justify-self-center text-[var(--public-highlight)]"
                          strokeWidth={1.55}
                          aria-hidden="true"
                        />
                        <span className="min-w-0">{label}</span>
                      </dt>
                      {/* The `dt` above spans both columns, so an unplaced `dd`
                          auto-flows into the 44px icon column and wraps the
                          value to four lines. Pin it under the label instead. */}
                      <dd className="col-start-2 mt-1 min-w-0 font-display text-[20px] leading-[1.15] text-[var(--public-foreground)]">
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>

                <p className="mt-5 flex items-center gap-3 font-display text-[15px] leading-[1.4] text-[var(--public-foreground-soft)]">
                  <ShieldCheck
                    className="size-6 shrink-0 text-[var(--public-highlight)]"
                    strokeWidth={1.55}
                    aria-hidden="true"
                  />
                  {academy.application.reassurance}
                </p>
              </section>
            </div>
          </div>
        </section>

        {/*
          The band pays the rail out from under its own sparkle, so at tablet
          its padding is pinned rather than clamped: the icon is 32px, and 48 +
          16 puts its centre on 64, which is the riser's centreline and the
          export's spine. The clamp drifts that centre from 72 at 768 to 77.375
          at 1023 while the riser stays on 64, so no single offset holds across
          the band. Above 64rem the descender leaves from the outer gutter
          instead and the clamp returns.
        */}
        <div className="hidden bg-[var(--public-primary)] px-5 py-6 text-[var(--public-on-inverse)] sm:px-10 md:block md:px-12 lg:px-[clamp(56px,6vw,64px)] lg:py-7">
          <div className="mx-auto flex w-full max-w-[1144px] flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="flex max-w-[650px] items-center gap-6 font-display text-[clamp(19px,2.2vw,24px)] leading-[1.25]">
              <Sparkle
                className="size-8 shrink-0 text-[var(--public-highlight)]"
                strokeWidth={1.25}
                aria-hidden="true"
              />
              A two-evening rhythm that shapes how you learn, express, and live
              with purpose.
            </p>
            <a
              href="#apply"
              className={`${PUBLIC_LINK} gap-4 self-start font-mono text-[9px] font-semibold uppercase tracking-[0.2em] text-[var(--public-on-inverse)] transition-colors hover:text-[var(--public-on-inverse-accent)] sm:self-auto`}
            >
              Apply for the 2026-27 academic year
              <ArrowRight
                className="size-4 text-[var(--public-highlight)]"
                strokeWidth={1.75}
                aria-hidden="true"
              />
            </a>
          </div>
        </div>

        {/*
          Two of this section's paddings are rail geometry at tablet, where
          there is no outer gutter to run the descender down: the copy indents
          to 88 so the riser can hold the export's own x, and the 88 below it
          halves to 48 because the rhythm section's stage field no longer
          carries a vestigial 44 on top of it. Both revert at 64rem, where the
          descender runs its own gutter and About returns to the content edge.
        */}
        <section
          aria-labelledby="about-heading"
          className="relative border-b border-[var(--public-rule)] px-5 pb-10 pt-12 sm:px-10 md:border-b-0 md:px-12 md:pb-12 md:pl-[88px] md:pt-[104px] lg:pb-9 lg:pl-12"
        >
          {/*
            The rail's head. It leaves the lapis band directly above and runs
            the length of this section, which is why it is drawn here: its
            length is this section's height. Geometry and the widths each pipe
            is drawn at live in styles/components/rhythm-rail.css.

            Every one is stroked, for the same reason the terminus pipes are:
            the exported run each hands over to is a stroked path too, so both
            sides of the seam rasterise the same way at a fractional width. The
            wrapper clips each riser at this section's top edge, which is the
            band's bottom edge.
          */}
          <div aria-hidden="true" className="rhythm-rail-descender">
            {/*
              Tablet: the export enters the section as a vertical spine, so
              there is no corner to turn and the riser simply continues the
              spine's own x upward. A stretched viewBox, because a vertical
              line's thickness is its x scale alone.
            */}
            <svg
              className="rhythm-rail-descender__pipe rhythm-rail-descender__pipe--tablet"
              viewBox="0 0 1 1"
              preserveAspectRatio="none"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <line x1="0.5" y1="0" x2="0.5" y2="1" stroke="currentColor" strokeWidth={1} />
            </svg>
            {DESCENDER_PIPES.map((pipe) => (
              <svg
                key={pipe.frame}
                className={`rhythm-rail-descender__pipe rhythm-rail-descender__pipe--${pipe.frame}`}
                viewBox={pipe.viewBox}
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d={pipe.d}
                  stroke="currentColor"
                  strokeWidth={pipe.width}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ))}
          </div>

          <div className="mx-auto w-full max-w-[1144px] md:grid md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:gap-16">
            <div>
              <p className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.2em] text-[var(--public-primary-deep)] md:text-[10px]">
                {academy.about.eyebrow}
              </p>
              <h2
                id="about-heading"
                className="mt-4 font-display text-[36px] font-semibold leading-none tracking-[-0.03em] text-[var(--public-foreground)] md:mt-5 md:max-w-[14ch] md:text-[clamp(44px,4.9vw,60px)] md:leading-[0.98] md:tracking-[-0.035em]"
              >
                {academy.about.h2}
              </h2>
            </div>

            <div className="mt-6 flex flex-col gap-5 md:mt-0 md:gap-7 md:pt-1.5">
              {academy.about.paragraphs.map((paragraph, index) =>
                index === academy.about.paragraphs.length - 1 ? (
                  <p
                    key={paragraph}
                    className="border-l-2 border-[var(--public-highlight)] pl-4 font-display text-[22px] leading-[1.38] text-[var(--public-primary-deep)] md:max-w-[44ch] md:pl-[22px] md:text-[clamp(21px,1.8vw,25px)] md:leading-[1.4]"
                  >
                    {paragraph}
                  </p>
                ) : (
                  <p
                    key={paragraph}
                    className="font-display text-[18px] leading-[1.55] text-[var(--public-foreground-soft)] md:max-w-[49ch] md:text-[clamp(18px,1.6vw,21px)]"
                  >
                    {paragraph}
                  </p>
                ),
              )}
            </div>
          </div>
        </section>

        <div className="relative overflow-hidden">
          <section aria-labelledby="approach-heading" className="rhythm-section relative z-10">
            <div className="rhythm-section__content">
              {/*
                No longer displayed: the rail now introduces the section. It
                stays in the document because it is this landmark's accessible
                name (`aria-labelledby` above), which deleting it would strip.
              */}
              <h2 id="approach-heading" className="sr-only">
                The Murtada rhythm
              </h2>

              <div className="rhythm-stage-field">
                <RhythmRail />
                <ol
                  aria-label="Three stages of formation"
                  className="rhythm-stage-list"
                >
                  {academy.approach.items.map((item, index) => (
                    <li
                      key={item.title}
                      className="rhythm-stage"
                      data-rhythm-stage={index + 1}
                    >
                      <article className="rhythm-stage__article">
                        <div className="rhythm-stage__title-row">
                          <span className="rhythm-stage__number">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                          <h3
                            aria-label={item.title}
                            className="rhythm-stage__title"
                          >
                            {item.lines.map((line) => (
                              <span key={line} className="block">
                                {line}
                              </span>
                            ))}
                          </h3>
                        </div>
                        <p className="rhythm-stage__body">{item.body}</p>
                      </article>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </section>
        </div>

        {/*
          From here to the submit button there is one ground and no seam. The
          rail arrives at this section's top edge and continues as the timeline
          spine — below 64rem down the left margin, at 64rem down the right,
          where the stops stack. See styles/components/rail-terminus.css.
        */}
        <section
          id="path"
          aria-labelledby="path-heading"
          className="path-section scroll-mt-20 bg-[var(--public-primary-deep)] pb-10 pt-12 text-[var(--public-on-inverse)] md:pb-16 md:pt-[72px]"
        >
          <div className="path-content mx-auto w-full max-w-[1144px] lg:grid lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1fr)] lg:gap-16">
            <div className="md:grid md:grid-cols-[minmax(0,0.85fr)_minmax(0,1fr)] md:items-end md:gap-16 lg:block">
              <div>
                <p className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.2em] text-[var(--public-highlight)] md:text-[10px]">
                  {academy.admissions.eyebrow}
                </p>
                <h2
                  id="path-heading"
                  className="mt-4 font-display text-[36px] font-semibold leading-none tracking-[-0.03em] text-[var(--public-on-inverse)] md:mt-[18px] md:text-[clamp(42px,4.5vw,56px)] md:leading-[0.98] md:tracking-[-0.035em]"
                >
                  {academy.admissions.h2}
                </h2>
              </div>
              <p className="mt-4 font-display text-[17px] leading-[1.5] text-[var(--public-on-inverse-accent)] md:mt-0 md:max-w-[46ch] md:text-[clamp(18px,1.7vw,21px)] lg:mt-[22px] lg:max-w-[27rem]">
                {academy.admissions.lede}
              </p>
            </div>

            {/* Named stops on a line — no numerals. The rhythm owns 01/02/03. */}
            <ol className="path-stops">
              {academy.admissions.steps.map((step, index) => (
                <li key={step.title} className="path-stop">
                  <div className="path-stop__title-row">
                    <h3 className="font-display text-[24px] font-semibold leading-[1.05] text-[var(--public-on-inverse)] lg:text-[30px]">
                      {step.title}
                    </h3>
                    <span aria-hidden="true" className="path-stop__leader" />
                  </div>
                  <p className="path-stop__body mt-1.5 text-[15px] leading-[1.5] text-[var(--public-on-inverse-accent)] md:text-[16px] lg:mt-2.5">
                    {step.body}
                  </p>
                  <span aria-hidden="true" className="path-stop__dot" />
                  {index === 0 ? (
                    <svg
                      aria-hidden="true"
                      className="path-spine path-spine--riser-vector"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <line x1="50%" x2="50%" y1="0" y2="100%" />
                      <circle
                        className="path-spine--riser-dot"
                        cx="50%"
                        cy="100%"
                        r="6.5"
                      />
                    </svg>
                  ) : null}
                  {index < academy.admissions.steps.length - 1 ? (
                    <span
                      aria-hidden="true"
                      className="path-spine path-spine--segment"
                    />
                  ) : null}
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section
          id="apply"
          aria-labelledby="apply-heading"
          className="relative isolate scroll-mt-20 overflow-hidden bg-[var(--public-primary-deep)] px-5 pb-12 pt-2 text-[var(--public-on-inverse)] sm:px-10 md:px-12 md:pb-20 md:pt-0"
          style={applicationTheme}
        >
          <img
            src="/brand/murtada/logo-mark-green.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-[60px] -right-[70px] -z-[1] w-[260px] max-w-none opacity-[0.18] md:-bottom-[120px] md:-right-[120px] md:w-[520px] md:opacity-[0.16]"
          />

          {/*
            Document order is facts → heading → form, which is the phone's
            reading order: the table closes out "A clear path to join", then the
            application begins. The desktop places the same three by row and
            column rather than reordering them, so nothing depends on `order`.
          */}
          <div className="mx-auto w-full max-w-[1144px] md:grid md:grid-cols-[300px_minmax(0,1fr)] md:items-start md:gap-x-16 md:gap-y-14">
            <aside className="mb-8 border border-[var(--public-highlight)]/35 px-[18px] py-5 md:col-start-1 md:row-start-2 md:mb-0 md:px-[22px] md:py-6">
              <h3 className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.2em] text-[var(--public-highlight)] md:text-[10px]">
                Everything to decide.
              </h3>
              <dl className="mt-3.5 md:mt-4">{decisionFacts}</dl>
              <p className="mt-[18px] hidden items-start gap-2.5 font-display text-[15px] leading-[1.4] text-[var(--public-on-inverse-accent)] md:flex">
                <ShieldCheck
                  className="mt-px size-5 shrink-0 text-[var(--public-highlight)]"
                  strokeWidth={1.55}
                  aria-hidden="true"
                />
                {academy.application.reassurance}
              </p>
            </aside>

            <div className="border-t border-[var(--public-highlight)]/45 pt-8 md:col-span-2 md:row-start-1 md:grid md:grid-cols-2 md:items-end md:gap-16 md:pt-14">
              <div>
                <p className="font-mono text-[9px] font-semibold uppercase tracking-[0.2em] text-[var(--public-highlight)] md:text-[10px]">
                  {academy.application.eyebrow}
                </p>
                <h2
                  id="apply-heading"
                  className="mt-3.5 font-display text-[36px] font-semibold leading-none tracking-[-0.03em] text-[var(--public-on-inverse)] md:mt-[18px] md:text-[clamp(42px,4.5vw,56px)] md:leading-[0.98] md:tracking-[-0.035em]"
                >
                  {academy.application.h2}
                </h2>
              </div>
              <div>
                <p className="mt-3.5 font-display text-[17px] leading-[1.5] text-[var(--public-on-inverse-accent)] md:mt-0 md:max-w-[50ch] md:text-[clamp(18px,1.6vw,20px)]">
                  {academy.application.lede}
                </p>
                <div className="mt-[18px] flex flex-wrap items-center gap-x-5 gap-y-2.5 font-mono text-[10px] tracking-[0.06em] text-[var(--public-on-inverse-accent)] md:gap-x-7 md:text-[10.5px]">
                  <span className="inline-flex items-center gap-2">
                    <Clock3
                      className="size-[15px] shrink-0 text-[var(--public-highlight)] md:size-4"
                      strokeWidth={1.65}
                      aria-hidden="true"
                    />
                    16 fields • about 4 minutes
                  </span>
                  {content.footer.email && (
                    <a
                      href={`mailto:${content.footer.email}`}
                      className="inline-flex items-center gap-2 text-[var(--public-on-inverse)] transition-colors hover:text-[var(--public-highlight)]"
                    >
                      <Mail
                        className="size-[15px] shrink-0 text-[var(--public-highlight)] md:size-4"
                        strokeWidth={1.45}
                        aria-hidden="true"
                      />
                      {content.footer.email}
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div
              ref={applicationFormRef}
              className={cn(
                "md:col-start-2 md:row-start-2",
                "[&_form]:grid [&_form]:grid-cols-1 [&_form]:gap-y-7 [&_form>*]:!mt-0 md:[&_form]:gap-y-10",
                // The 300px aside already claims most of a 768px viewport
                // (300 aside + 64 gap leaves ~308 for the whole form column) —
                // splitting turnstile+button into their own 300px+1fr pair
                // needs room the tablet breakpoint doesn't have. Gate on lg.
                "lg:[&_form]:grid-cols-[300px_minmax(0,1fr)] lg:[&_form]:gap-x-4 lg:[&_form>*]:col-span-2",
                "[&_[data-apply-section]]:items-start [&_[data-apply-section]]:gap-x-7 [&_[data-apply-section]]:gap-y-[18px] [&_[data-apply-section]]:border-t-0 [&_[data-apply-section]]:pt-0 md:[&_[data-apply-section]]:gap-y-[22px]",
                // Section headings: a label and a rule. No numeral.
                "[&_[data-apply-section-heading]]:w-full [&_[data-apply-section-heading]]:gap-4 [&_[data-apply-section-label]]:font-mono [&_[data-apply-section-label]]:text-[10px] [&_[data-apply-section-label]]:font-semibold [&_[data-apply-section-label]]:uppercase [&_[data-apply-section-label]]:tracking-[0.18em] [&_[data-apply-section-label]]:text-[var(--public-highlight)]",
                "[&_[data-apply-section-rule]]:h-px [&_[data-apply-section-rule]]:min-w-6 [&_[data-apply-section-rule]]:flex-1 [&_[data-apply-section-rule]]:border-0 [&_[data-apply-section-rule]]:bg-[var(--public-highlight)]/40",
                "[&_[data-apply-confirm-hint]]:sr-only [&_label[data-apply-qualified]]:flex-wrap [&_label[data-apply-qualified]]:items-baseline [&_label[data-apply-qualified]]:gap-x-1 [&_label[data-apply-qualified]]:gap-y-1 [&_[data-apply-helper]]:basis-full [&_[data-apply-helper]]:text-[13px] [&_[data-apply-helper]]:font-normal [&_[data-apply-helper]]:leading-[1.35] [&_[data-apply-helper]]:text-[var(--public-on-inverse-accent)] [&_[data-apply-optional]]:text-[13px] [&_[data-apply-optional]]:font-normal",
                "[&_label]:text-[13px] [&_label]:font-semibold [&_label]:tracking-normal [&_label]:text-[var(--public-on-inverse)] md:[&_label]:text-[13.5px]",
                // Boxed fields on the dark ground: an underline disappears
                // into deep green, and half the form is empty at rest.
                "[&_[data-slot=input]]:bg-[var(--public-on-inverse)]/[0.06] [&_[data-slot=input]]:placeholder:text-[var(--public-on-inverse-accent)] [&_[data-slot=input]]:placeholder:opacity-70",
                "[&_[data-slot=select-trigger]]:bg-[var(--public-on-inverse)]/[0.06] [&_[data-slot=select-trigger]>svg]:text-[var(--public-highlight)]",
                "[&_[data-slot=textarea]]:bg-[var(--public-on-inverse)]/[0.06] [&_[data-slot=textarea]]:placeholder:text-[var(--public-on-inverse-accent)] [&_[data-slot=textarea]]:placeholder:opacity-70",
                // Turnstile and submit share a row once there is width for it:
                // both drop out of the form's default col-span-2 to sit in the
                // fixed 300px / remaining-space columns set on `[&_form]` above.
                "[&_[data-apply-turnstile]]:lg:w-[300px] [&_[data-apply-turnstile]]:lg:col-span-1 [&_[data-apply-turnstile]]:lg:self-center",
                "[&_form>[data-slot=button]]:w-full [&_form>[data-slot=button]]:lg:col-span-1 [&_form>[data-slot=button]]:lg:self-center",
                "[&_form>p.text-center]:mx-auto [&_form>p.text-center]:max-w-[760px] [&_form>p.text-center]:leading-5",
              )}
            >
              {applicationForm}
            </div>
          </div>
        </section>
      </main>

      <a
        href="#apply"
        aria-label="Start application for the 2026-27 academic year"
        aria-hidden={applicationFormInView}
        tabIndex={applicationFormInView ? -1 : undefined}
        className={cn(
          PUBLIC_FOCUS,
          "fixed inset-x-0 bottom-0 z-50 flex min-h-[60px] items-center justify-between gap-4 bg-[var(--public-primary)] px-5 font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[var(--public-on-inverse)] transition-[transform,opacity] duration-[var(--motion-fast)] ease-[var(--ease-standard)] motion-reduce:transition-none md:hidden",
          applicationFormInView
            ? "pointer-events-none translate-y-full opacity-0"
            : "translate-y-0 opacity-100",
        )}
      >
        <span>Apply · 2026-27</span>
        <span className="inline-flex items-center gap-2.5">
          Start
          <ArrowRight
            className="size-[18px] text-[var(--public-highlight)]"
            strokeWidth={1.7}
            aria-hidden="true"
          />
        </span>
      </a>

      <footer className="border-t border-[var(--public-rule)] bg-[var(--public-background)] px-5 py-5 sm:px-10">
        <div className="mx-auto flex w-full max-w-[1144px] flex-col gap-3 text-[11px] text-[var(--public-foreground-muted)] sm:flex-row sm:items-center sm:justify-between">
          <p>
            <span className="font-display text-[15px] font-semibold text-[var(--public-foreground)]">
              {brandName}
            </span>{" "}
            · {academyAffiliation}
          </p>
          {showSignIn && (
            <Link
              to="/sign-in"
              className={`${PUBLIC_LINK} font-mono text-[9px] font-semibold uppercase tracking-[0.18em] text-[var(--public-primary-deep)] hover:text-[var(--public-primary)]`}
            >
              Student and faculty sign in
            </Link>
          )}
        </div>
      </footer>
    </div>
  );
}
